import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { DollarSign, Package, ChefHat, ArrowRight, X, CheckCircle } from 'lucide-react';

export const OnboardingModal = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const { completeOnboarding, skipOnboarding } = useSubscription();

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      completeOnboarding();
      onClose();
    }
  };

  const handleSkip = () => {
    skipOnboarding();
    onClose();
  };

  const steps = [
    {
      icon: DollarSign,
      title: "Set your business basics",
      content: (
        <div className="space-y-4">
          <p className="text-[#6B7280]">
            Start by setting your cost parameters in Settings:
          </p>
          <div className="bg-[#F9FAFB] p-4 rounded-lg space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-[#2C5F7C] text-white rounded-full flex items-center justify-center text-sm font-medium">1</div>
              <div>
                <div className="font-medium">Labour Rate (AUD/hour)</div>
                <div className="text-sm text-[#6B7280]">What you pay yourself or staff per hour. This gets factored into every recipe.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-[#2C5F7C] text-white rounded-full flex items-center justify-center text-sm font-medium">2</div>
              <div>
                <div className="font-medium">Overhead Percentage</div>
                <div className="text-sm text-[#6B7280]">Covers rent, utilities, insurance. Typically 10-15% for home bakers.</div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      icon: Package,
      title: "Add your ingredients",
      content: (
        <div className="space-y-4">
          <p className="text-[#6B7280]">
            Build your ingredient library with accurate costs:
          </p>
          <div className="bg-[#F9FAFB] p-4 rounded-lg space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#22C55E] mt-0.5" />
              <div>
                <div className="font-medium">Pack Size</div>
                <div className="text-sm text-[#6B7280]">How much you buy at a time (e.g., 1kg flour)</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#22C55E] mt-0.5" />
              <div>
                <div className="font-medium">Pack Price</div>
                <div className="text-sm text-[#6B7280]">What you actually paid for it</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#22C55E] mt-0.5" />
              <div>
                <div className="font-medium">Unit Cost</div>
                <div className="text-sm text-[#6B7280]">Calculated automatically - cost per gram/ml</div>
              </div>
            </div>
          </div>
          <p className="text-sm text-[#9B7EBD] italic">
            We've added some sample ingredients to get you started!
          </p>
        </div>
      )
    },
    {
      icon: ChefHat,
      title: "Create your first recipe",
      content: (
        <div className="space-y-4">
          <p className="text-[#6B7280]">
            Combine ingredients and let CostCraft calculate your pricing:
          </p>
          <div className="bg-[#F9FAFB] p-4 rounded-lg space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#22C55E] mt-0.5" />
              <div>
                <div className="font-medium">Yield</div>
                <div className="text-sm text-[#6B7280]">How many units this batch makes (cookies, cakes, etc.)</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#22C55E] mt-0.5" />
              <div>
                <div className="font-medium">Labour Time</div>
                <div className="text-sm text-[#6B7280]">Prep, bake, and finishing time</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#22C55E] mt-0.5" />
              <div>
                <div className="font-medium">Packaging Cost</div>
                <div className="text-sm text-[#6B7280]">Cost per unit for boxes, bags, labels</div>
              </div>
            </div>
          </div>
          <div className="bg-[#EFF6FF] p-4 rounded-lg">
            <div className="font-medium text-[#2C5F7C] mb-1">✨ Automatic Pricing</div>
            <div className="text-sm text-[#6B7280]">
              CostCraft calculates retail and wholesale prices based on your costs and target margins.
            </div>
          </div>
        </div>
      )
    }
  ];

  const currentStep = steps[step - 1];
  const StepIcon = currentStep.icon;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden" data-testid="onboarding-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2C5F7C] to-[#9B7EBD] p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm opacity-80">Step {step} of 3</div>
            <button
              onClick={handleSkip}
              className="text-white/80 hover:text-white transition-colors"
              data-testid="skip-onboarding-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <StepIcon className="w-6 h-6" />
            </div>
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {currentStep.title}
            </h2>
          </div>
          {/* Progress bar */}
          <div className="flex gap-2 mt-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= step ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {currentStep.content}
        </div>

        {/* Footer */}
        <div className="p-6 pt-0 flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-[#6B7280] hover:text-[#1F2937] text-sm transition-colors"
            data-testid="skip-for-now-btn"
          >
            Skip for now
          </button>
          <Button
            onClick={handleNext}
            className="bg-[#2C5F7C] hover:bg-[#1E4A60] text-white px-6 rounded-full"
            data-testid="onboarding-next-btn"
          >
            {step === 3 ? "Get Started" : "Next"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};
